import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type QueryKey,
} from "@tanstack/react-query";
import { useEffect } from "react";
import { useApiClient } from "./ApiProvider";
import type {
  AdoptServiceInput,
  ChangePasswordInput,
  ComposeImportResult,
  CorpusBindingInput,
  CreateAgentSessionInput,
  CreateAlertRuleInput,
  CreateConnectionInput,
  CreateDomainInput,
  CreateEnrollTokenInput,
  CreateEnvironmentInput,
  CreateInstanceInput,
  CreateLlmCredentialInput,
  CreatePlacementInput,
  CreateProjectInput,
  CreateProviderCredentialInput,
  CreateServiceInput,
  CreateUserInput,
  EnrollToken,
  ListAgentSessionsParams,
  ListAuditParams,
  ListContainersParams,
  Page,
  PageParams,
  PromoteServiceInput,
  PromoteSessionInput,
  PromoteSessionResult,
  ProposeImportParams,
  RedeployOptions,
  RotateLlmCredentialKeyInput,
  RotateProviderCredentialInput,
  RunSessionTaskInput,
  SetNodeEnvironmentInput,
  SSOConfigInput,
  SSOInvite,
  SSOInviteInput,
  SSOTestConnectionInput,
  SSOTestConnectionResult,
  UpdateAlertRuleInput,
  UpdateEnvironmentInput,
  UpdateLlmCredentialConfigInput,
  UpdateNodeInput,
  UpdatePlacementInput,
  UpdateProjectInput,
  UpdateServiceInput,
  UpdateUserInput,
} from "./client";
import type { ApiError } from "./errors";
import { isNotFound } from "./errors";
import type {
  AdoptPreview,
  AgentSession,
  AlertInstance,
  AlertRule,
  ApplyImportResult,
  AuditVerification,
  Connection,
  ConnectionStatus,
  Container,
  ContainerEvent,
  CorpusBinding,
  GovernanceFinding,
  GovernancePolicy,
  Deployment,
  DeploymentDetail,
  DeployPreview,
  Domain,
  Environment,
  ImportProposal,
  Node,
  HealthCell,
  Instance,
  LlmCredentialSummary,
  Placement,
  Project,
  ProposedImportItem,
  ProviderCredentialSummary,
  SecretSummary,
  Service,
  SSOConfig,
  User,
} from "./types";

/**
 * Central query-key factory so every hook and cache write agrees on shape.
 *
 * Every list key carries an explicit `"list"` segment and every single-item
 * key an explicit `"detail"`/id segment, kept distinct on purpose: list
 * hooks below append `[cursor, limit]` onto a *list* key per page (FE-H2),
 * and several cache writes use `setQueriesData`/`invalidateQueries` with a
 * *prefix* match against these keys — without the "list" vs "detail" split,
 * a prefix match against `containers()` would also sweep up
 * `container(nodeId, containerId)` detail entries (same leading
 * segments) and apply a list-shaped (`Page<T>`) updater to a bare `T`.
 */
export const queryKeys = {
  containers: () => ["containers", "list"] as const,
  // A container id is only unique per node, so its cache key includes nodeId too
  // (mirrors the real backend's routing — see client.ts).
  container: (nodeId: string, containerId: string) =>
    ["containers", "detail", nodeId, containerId] as const,
  nodes: () => ["nodes", "list"] as const,
  // ---- Hosts: the unified fleet object (host/deploy UX review, #526) ----
  hosts: () => ["hosts", "list"] as const,
  host: (id: string) => ["hosts", "detail", id] as const,
  secrets: () => ["secrets", "list"] as const,
  // ---- Admin/user portal ----
  users: () => ["users", "list"] as const,
  account: () => ["account"] as const,
  ssoConfig: () => ["sso-config"] as const,
  audit: () => ["audit", "list"] as const,
  // ---- Hero flow ----
  projects: () => ["projects", "list"] as const,
  project: (id: string) => ["projects", "detail", id] as const,
  // ---- Corpus (ADR-0052 read / ADR-0053 §3 binding) ----
  corpus: (projectId: string) => ["corpus", "list", projectId] as const,
  corpusBinding: (projectId: string) => ["corpus", "binding", projectId] as const,
  services: (projectId: string) => ["services", "list", projectId] as const,
  service: (projectId: string, id: string) =>
    ["services", "detail", projectId, id] as const,
  deployments: (projectId: string, serviceId: string) =>
    ["deployments", "list", projectId, serviceId] as const,
  deployment: (projectId: string, serviceId: string, deploymentId: string) =>
    ["deployments", "detail", projectId, serviceId, deploymentId] as const,
  // Read-only plan/volume-risk preview (ADR-0031 §2d) — its own key family (not folded
  // under `deployments`, which is the deploy timeline) since it's invalidated on a
  // different trigger (opening the rollback dialog), never on a deploy/rollback mutation.
  deployPreview: (projectId: string, serviceId: string) =>
    ["deployPreview", projectId, serviceId] as const,
  // ---- The Placement Graph (ADR-0025) ----
  placements: (projectId: string, serviceId: string) =>
    ["placements", "list", projectId, serviceId] as const,
  nodePlacements: (nodeId: string) => ["placements", "byNode", nodeId] as const,
  connections: (projectId: string) => ["connections", "list", projectId] as const,
  // ADR-0029 P3-5 (see ConnectionStatus's doc comment in types.ts) — one
  // batch key per PROJECT (the real endpoint is project-level, not
  // per-connection), deliberately its own key family rather than folded
  // under `connections`, since it's refetched/invalidated on a completely
  // different trigger (a live reachability check) than the connections'
  // own declared-topology data.
  connectionStatuses: (projectId: string) =>
    ["connectionStatuses", projectId] as const,
  // ---- Service domain bindings (ADR-0026 §3/§5) ----
  domains: (projectId: string, serviceId: string) =>
    ["domains", "list", projectId, serviceId] as const,
  providerCredentials: () => ["providerCredentials", "list"] as const,
  llmCredentials: () => ["llmCredentials", "list"] as const,
  agentSessions: () => ["agentSessions", "list"] as const,
  agentSession: (id: string) => ["agentSessions", "detail", id] as const,
  providerCatalog: (credentialId: string) =>
    ["providerCatalog", credentialId] as const,
  instances: (projectId?: string) =>
    ["instances", "list", projectId ?? null] as const,
  instance: (id: string) => ["instances", "detail", id] as const,
  // ---- Fleet health matrix (ADR-0030 P4-2) ----
  health: () => ["health", "list"] as const,
  // ---- Fleet-health alerting (ADR-0030 P4-5) ----
  alertRules: () => ["alertRules", "list"] as const,
  alertInstances: (ruleId: string) =>
    ["alertInstances", "list", ruleId] as const,
  // ---- First-class environments (ADR-0035 §1) ----
  // Tenant/session-scoped: the routes take their tenant only from the session (S-17).
  environments: () => ["environments", "list"] as const,
  environment: (id: string) => ["environments", "detail", id] as const,
  // Billing is environment-wide like users/account: the server scopes it to the
  // session's tenant, so there is no fleet id in the key.
  subscription: () => ["billing", "subscription"] as const,
  entitlement: () => ["billing", "entitlement"] as const,
};

/** Appends the page params any paginated list's query key includes, so
 * every cursor/limit combination gets its own cache entry (FE-H2) while
 * `queryKeys.<thing>()` alone stays a stable prefix for
 * invalidate/patch across every page. */
function pageKeySuffix(params: PageParams): readonly [string | null, number | null] {
  return [params.cursor ?? null, params.limit ?? null];
}

/** How often to poll while a service deploy/instance provision is mid-flight
 * (P-1 seam, no event stream for these yet — same gap as containers, see
 * subscribeToContainerEvents). Idle otherwise: a deploy/create already
 * invalidates its query on success for an immediate refresh. */
const MID_DEPLOY_POLL_MS = 2000;

export function useContainers(params: ListContainersParams = {}) {
  const client = useApiClient();
  return useQuery({
    // `unmanaged`/`serviceId` get their own key segments (not folded into pageKeySuffix,
    // which is only cursor/limit) so different filter combinations cache separately —
    // toggling a filter never shows a stale page from a different one.
    queryKey: [
      ...queryKeys.containers(),
      params.nodeId ?? null,
      params.unmanaged ?? false,
      params.serviceId ?? null,
      ...pageKeySuffix(params),
    ],
    queryFn: () => client.listContainers({ ...params }),
    enabled: true,
    // The real control plane has no event stream yet (P-1 seam deferred — see
    // realClient.ts's subscribeToContainerEvents). A start/stop/restart already
    // invalidates this query for an immediate refresh (below); this modest
    // background refetch is the one deliberate exception to "event-driven, not
    // polled" so the list still catches changes made from outside this UI (e.g.
    // `docker` on the host). Do not add polling anywhere else.
    refetchInterval: 20_000,
    // Keeps the current page's rows on screen while the next page loads
    // rather than flashing to a loading state on every Previous/Next (FE-H2).
    placeholderData: keepPreviousData,
  });
}

export function useContainer(nodeId: string | undefined, containerId: string | undefined) {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.container(nodeId ?? "", containerId ?? ""),
    queryFn: () => client.getContainer(nodeId as string, containerId as string),
    enabled: Boolean(nodeId) && Boolean(containerId),
  });
}

/**
 * `options.refetchInterval` is an explicit opt-in, not a default (P-1: the
 * UI is event-driven, not polled) — it exists for the ONE caller with no
 * better event source: the deploy wizard's "use this laptop" step, which is
 * waiting on a human running a shell command on a machine the browser has no
 * other way to observe. Every other caller leaves it unset (no polling).
 */
export function useNodes(params: PageParams = {}, options?: { refetchInterval?: number | false }) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...queryKeys.nodes(), ...pageKeySuffix(params)],
    queryFn: () => client.listNodes({ ...params }),
    enabled: true,
    refetchInterval: options?.refetchInterval ?? false,
    placeholderData: keepPreviousData,
  });
}

// ---- Hosts: the unified fleet object (host/deploy UX review, #526) ----
// Read-only projection over provisioning + live-agent state (see Host's doc
// comment in types.ts) — no event stream yet (P-1 seam, same gap as
// useInstances/useServices), so this polls a bit faster while any row is
// still mid-transition (provisioning, not yet enrolled, or being torn down).

const HOST_TRANSITIONAL_STATES = new Set(["provisioning", "enrolling", "decommissioning"]);

export function useHosts(params: PageParams = {}) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...queryKeys.hosts(), ...pageKeySuffix(params)],
    queryFn: () => client.listHosts({ ...params }),
    enabled: true,
    refetchInterval: (query) => {
      const items = query.state.data?.items;
      return items?.some((h) => HOST_TRANSITIONAL_STATES.has(h.state)) ? MID_DEPLOY_POLL_MS : 20_000;
    },
    placeholderData: keepPreviousData,
  });
}

export function useHost(id: string | undefined) {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.host(id ?? ""),
    queryFn: () => client.getHost(id as string),
    enabled: Boolean(id),
    refetchInterval: (query) =>
      query.state.data && HOST_TRANSITIONAL_STATES.has(query.state.data.state) ? MID_DEPLOY_POLL_MS : false,
  });
}

/** Admin-only server-side (`node:update`); today only sets/clears
 * `connectAddress` (ADR-0029 §5). Invalidates the fleet's node list on
 * success so the edited node's row (and any other view reading it) picks up
 * the new value without a manual refetch (P-1). */
export function useUpdateNode() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<Node, ApiError, { nodeId: string; input: UpdateNodeInput }>({
    mutationFn: ({ nodeId, input }) => client.updateNode(nodeId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.nodes() });
    },
  });
}

/** The Fleet "Change environment" action (IA slice 2, #605's `PATCH
 * /api/nodes/{nodeId}/environment`) — admin+session-only server-side.
 * Invalidates both the raw node list AND the unified Hosts read model
 * (`Host.environmentId`/`environmentName` are a server-side projection of
 * the same underlying node, A-5) so a machine's detail page and the
 * Machines list both pick up the move without a manual refetch. */
export function useSetNodeEnvironment() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<Node, ApiError, { nodeId: string; input: SetNodeEnvironmentInput }>({
    mutationFn: ({ nodeId, input }) => client.setNodeEnvironment(nodeId, input),
    onSuccess: (_updated, { nodeId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.nodes() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.hosts() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.host(nodeId) });
    },
  });
}

export function useSecrets(params: PageParams = {}) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...queryKeys.secrets(), ...pageKeySuffix(params)],
    queryFn: () => client.listSecrets({ ...params }),
    enabled: true,
    placeholderData: keepPreviousData,
  });
}

/**
 * Patches every cached page of the containers list (any cursor — FE-H2 means
 * more than one page can be cached at once) plus the single detail entry, in
 * place. Uses `setQueriesData`'s prefix match against `queryKeys.containers`
 * rather than a single `setQueryData` at one fixed key, since which page(s)
 * are cached (and which one holds this container) varies with how far the
 * operator has paged. Safe from cross-contaminating the detail cache (a bare
 * `Container`, not a `Page<Container>`) because `container()`'s key carries
 * a `"detail"` segment `containers()`'s doesn't share — see queryKeys above.
 */
function patchContainerCaches(
  queryClient: QueryClient,
  nodeId: string,
  containerId: string,
  patch: Partial<Container>,
) {
  queryClient.setQueryData<Container>(queryKeys.container(nodeId, containerId), (current) =>
    current ? { ...current, ...patch } : current,
  );
  queryClient.setQueriesData<Page<Container>>({ queryKey: queryKeys.containers() }, (current) => {
    if (!current) return current;
    return {
      ...current,
      items: current.items.map((c) => (c.id === containerId && c.nodeId === nodeId ? { ...c, ...patch } : c)),
    };
  });
}

interface ActionVariables {
  nodeId: string;
  containerId: string;
}

interface ActionContext {
  previousDetail?: Container;
  /** Every cached containers-list page as of `onMutate`, snapshotted by
   * exact key so `onError` can restore each one precisely — see
   * `patchContainerCaches`. */
  previousLists: Array<[QueryKey, Page<Container> | undefined]>;
  nodeId: string;
  containerId: string;
}

type ContainerActionKind = "start" | "stop" | "restart";

const OPTIMISTIC_COPY: Record<ContainerActionKind, { state: Container["state"]; status: string }> = {
  start: { state: "running", status: "Starting…" },
  stop: { state: "exited", status: "Stopping…" },
  restart: { state: "restarting", status: "Restarting…" },
};

function useContainerAction(kind: ContainerActionKind) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const action =
    kind === "start" ? client.startContainer : kind === "stop" ? client.stopContainer : client.restartContainer;

  return useMutation<Container, ApiError, ActionVariables, ActionContext>({
    mutationFn: ({ nodeId, containerId }) => action(nodeId, containerId),
    onMutate: async ({ nodeId, containerId }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.container(nodeId, containerId) });
      await queryClient.cancelQueries({ queryKey: queryKeys.containers() });
      const previousDetail = queryClient.getQueryData<Container>(queryKeys.container(nodeId, containerId));
      const previousLists = queryClient.getQueriesData<Page<Container>>({ queryKey: queryKeys.containers() });
      patchContainerCaches(queryClient, nodeId, containerId, OPTIMISTIC_COPY[kind]);
      return { previousDetail, previousLists, nodeId, containerId };
    },
    onError: (_error, _variables, context) => {
      if (!context) return;
      if (context.previousDetail) {
        queryClient.setQueryData(
          queryKeys.container(context.nodeId, context.containerId),
          context.previousDetail,
        );
      }
      for (const [key, data] of context.previousLists) {
        queryClient.setQueryData(key, data);
      }
    },
    onSuccess: (updated, { nodeId, containerId }) => {
      patchContainerCaches(queryClient, nodeId, containerId, updated);
      // No real event stream yet (P-1 seam deferred — see realClient.ts): the
      // direct cache write above already reflects this action's own result
      // immediately, and invalidating the list on top catches anything else
      // that changed underneath it (another operator, `docker` on the host,
      // etc.) with one extra fetch rather than a standing poll.
      void queryClient.invalidateQueries({ queryKey: queryKeys.containers() });
    },
  });
}

export function useStartContainer() {
  return useContainerAction("start");
}

export function useStopContainer() {
  return useContainerAction("stop");
}

export function useRestartContainer() {
  return useContainerAction("restart");
}

/**
 * The P-1 seam made concrete: subscribes to the (mock: simulated; real: not yet
 * implemented — see realClient.ts) event stream for a fleet and patches the
 * query cache directly rather than refetching. Mount once per active fleet (see
 * `AppShell`) and every list/detail view fed by `queryKeys.containers`/
 * `queryKeys.container` updates live with no polling involved once the real
 * event channel lands.
 */
export function useContainerEventsSubscription(): void {
  const client = useApiClient();
  const queryClient = useQueryClient();

  useEffect(() => {
    const unsubscribe = client.subscribeToContainerEvents((event: ContainerEvent) => {
      patchContainerCaches(queryClient, event.nodeId, event.containerId, {
        state: event.state,
        status: event.status,
      });
    });
    return unsubscribe;
  }, [client, queryClient]);
}

// ---- Secrets (write-only — see api/types.ts) ----
// Every mutation below invalidates the list on success rather than writing an
// optimistic patch: there is no event stream for secrets (P-1 seam, same gap
// as containers today) and, unlike a container action, there is no value to
// echo back into the cache anyway — only refreshed metadata.

export function useCreateSecret() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<SecretSummary, ApiError, { name: string; value: string }>({
    mutationFn: ({ name, value }) => client.createSecret({ name, value }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.secrets() });
    },
  });
}

export function useRotateSecret() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<SecretSummary, ApiError, { secretId: string; value: string }>({
    mutationFn: ({ secretId, value }) => client.rotateSecret(secretId, value),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.secrets() });
    },
  });
}

export function useDeleteSecret() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, { secretId: string }>({
    mutationFn: ({ secretId }) => client.deleteSecret(secretId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.secrets() });
    },
  });
}

// ---- Enrollment (C-6: "add a host") ----
// Deliberately no query-cache involvement: a join token is single-use,
// short-lived, shown once, and never persisted (S-13) — there is nothing here
// that belongs in a cache another view could read back.

export function useCreateEnrollToken() {
  const client = useApiClient();
  return useMutation<EnrollToken, ApiError, CreateEnrollTokenInput>({
    mutationFn: (input) => client.createEnrollToken(input),
  });
}

// ---- Admin: users (admin/user portal) ----
// Every mutation invalidates the list on success — same "no event stream yet,
// invalidate and refetch" shape as secrets above. `enabled` is threaded
// through so a page can call this hook unconditionally (rules of hooks) while
// still skipping the request entirely for a non-admin viewer (see UsersPage).

export function useUsers(params: PageParams = {}, options?: { enabled?: boolean }) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...queryKeys.users(), ...pageKeySuffix(params)],
    queryFn: () => client.listUsers(params),
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
  });
}

export function useCreateUser() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<User, ApiError, CreateUserInput>({
    mutationFn: (input) => client.createUser(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.users() });
    },
  });
}

export function useUpdateUser() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<User, ApiError, { id: string; input: UpdateUserInput }>({
    mutationFn: ({ id, input }) => client.updateUser(id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.users() });
    },
  });
}

export function useResetUserPassword() {
  const client = useApiClient();
  return useMutation<void, ApiError, { id: string; password: string }>({
    mutationFn: ({ id, password }) => client.resetUserPassword(id, password),
  });
}

export function useDeleteUser() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, { id: string }>({
    mutationFn: ({ id }) => client.deleteUser(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.users() });
    },
  });
}

// ---- OIDC / SSO admin (ADR-0037 PR-2b) ----

export function useSSOConfig() {
  const client = useApiClient();
  return useQuery({ queryKey: queryKeys.ssoConfig(), queryFn: () => client.getSSOConfig() });
}

export function useUpdateSSOConfig() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<SSOConfig, ApiError, SSOConfigInput>({
    mutationFn: (input) => client.updateSSOConfig(input),
    onSuccess: (updated) => {
      queryClient.setQueryData(queryKeys.ssoConfig(), updated);
    },
  });
}

/** Deliberately NOT cached (queryKey-less): every call must hit the live
 * check for the CURRENT (possibly unsaved) issuer/client-id the admin is
 * editing, never a stale prior result. */
export function useTestSSOConnection() {
  const client = useApiClient();
  return useMutation<SSOTestConnectionResult, ApiError, SSOTestConnectionInput>({
    mutationFn: (input) => client.testSSOConnection(input),
  });
}

export function useCreateSSOInvite() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<SSOInvite, ApiError, SSOInviteInput>({
    mutationFn: (input) => client.createSSOInvite(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.users() });
    },
  });
}

// ---- Account (the caller's own profile/credential) ----

export function useAccount() {
  const client = useApiClient();
  return useQuery({ queryKey: queryKeys.account(), queryFn: () => client.getAccount() });
}

export function useChangePassword() {
  const client = useApiClient();
  return useMutation<void, ApiError, ChangePasswordInput>({
    mutationFn: (input) => client.changePassword(input),
  });
}

// ---- Audit log (C-13) ----
// `placeholderData: keepPreviousData` keeps the current page's rows on screen
// while the next page loads instead of flashing to a loading state on every
// Next/Previous click — the list is still small and bounded (one page, P-6),
// this is purely a continuity nicety.

export function useAuditEvents(params: ListAuditParams, options?: { enabled?: boolean }) {
  const client = useApiClient();
  return useQuery({
    // `action` gets its own key segment (like `nodeId` on useContainers) so the
    // unfiltered Audit log page and a filtered view (e.g. Governance Decisions,
    // action: "governance.decision") cache separately — switching between them
    // never shows a stale page from the other.
    queryKey: [...queryKeys.audit(), params.action ?? null, ...pageKeySuffix(params)],
    queryFn: () => client.listAuditEvents({ ...params }),
    enabled: (options?.enabled ?? true),
    placeholderData: keepPreviousData,
  });
}

/**
 * The Governance Decisions view's data path (ADR-0058): the SAME audit-events query as
 * `useAuditEvents`, narrowed server-side to `action: "governance.decision"` — never a
 * second fetch mechanism or a client-side filter over the whole trail (A-5, reusing the
 * one audit read path). See `features/governance/governanceDecision.ts` for the
 * verdict/detail projection this feeds.
 */
export function useGovernanceDecisions(params: PageParams) {
  return useAuditEvents({ ...params, action: "governance.decision" });
}

export function useVerifyAuditChain() {
  const client = useApiClient();
  return useMutation<AuditVerification, ApiError, void>({
    mutationFn: () => client.verifyAuditChain(),
  });
}

// ---- Hero flow: project -> service -> deploy (+ Hetzner provisioning) ----
// Mirrors the real, merged backend contract (#63 projectsvc, #62
// provisioning) exactly — see the header notes in api/types.ts/client.ts.

export function useProjects(params: PageParams = {}) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...queryKeys.projects(), ...pageKeySuffix(params)],
    queryFn: () => client.listProjects({ ...params }),
    enabled: true,
    placeholderData: keepPreviousData,
  });
}

export function useProject(id: string | undefined) {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.project(id ?? ""),
    queryFn: () => client.getProject(id as string),
    enabled: Boolean(id),
  });
}

export function useCreateProject() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<Project, ApiError, CreateProjectInput>({
    mutationFn: (input) => client.createProject(input),
    onSuccess: (project) => {
      queryClient.setQueryData(queryKeys.project(project.id), project);
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects() });
    },
  });
}

export function useUpdateProject() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<Project, ApiError, { id: string; input: UpdateProjectInput }>({
    mutationFn: ({ id, input }) => client.updateProject(id, input),
    onSuccess: (project) => {
      queryClient.setQueryData(queryKeys.project(project.id), project);
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects() });
    },
  });
}

export function useDeleteProject() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, { id: string }>({
    mutationFn: ({ id }) => client.deleteProject(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects() });
    },
  });
}

// ---- Corpus (ADR-0052 read / ADR-0053 §3 binding) ----
// No event stream (P-1 seam, same gap as services/hosts): a corpus changes only when
// someone binds a repo or a Selkie handoff lands, neither of which happens from inside this
// browser tab except through the mutations below (which already invalidate on success), so
// this deliberately does NOT poll — unlike useServices/useHosts there is no in-flight
// server-side transition here worth catching between page loads.

export function useCorpus(projectId: string, params: PageParams = {}) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...queryKeys.corpus(projectId), ...pageKeySuffix(params)],
    queryFn: () => client.getCorpus(projectId, params),
    enabled: Boolean(projectId),
    placeholderData: keepPreviousData,
  });
}

/** `corpus_binding:view` is operator/admin only server-side (a viewer holds `corpus:view`
 * but not this) — callers pass `enabled: canManageFleet(role)` so a viewer's page never
 * fires a call the server can only ever 403 (see `auth/roleView.ts`). */
export function useCorpusBinding(projectId: string, options?: { enabled?: boolean }) {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.corpusBinding(projectId),
    queryFn: () => client.getCorpusBinding(projectId),
    enabled: Boolean(projectId) && (options?.enabled ?? true),
    // A 404 here means "not bound yet" — an expected, honest outcome (A-10), not a
    // transient failure — so don't burn retries/backoff on it like a real fault.
    retry: (failureCount, error) => !isNotFound(error) && failureCount < 2,
  });
}

export function useCreateCorpusBinding(projectId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<CorpusBinding, ApiError, CorpusBindingInput>({
    mutationFn: (input) => client.createCorpusBinding(projectId, input),
    onSuccess: (binding) => {
      queryClient.setQueryData(queryKeys.corpusBinding(projectId), binding);
      void queryClient.invalidateQueries({ queryKey: queryKeys.corpus(projectId) });
    },
  });
}

export function useUpdateCorpusBinding(projectId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<CorpusBinding, ApiError, CorpusBindingInput>({
    mutationFn: (input) => client.updateCorpusBinding(projectId, input),
    onSuccess: (binding) => {
      queryClient.setQueryData(queryKeys.corpusBinding(projectId), binding);
      void queryClient.invalidateQueries({ queryKey: queryKeys.corpus(projectId) });
    },
  });
}

// ---- Governing-agent findings (ADR-0059) ----
// The Decisions view's findings surface: a project's governing-agent objections. Like the corpus
// hooks there is no event stream — findings change only when the server-side producer appends one
// (not from this tab) or a human resolves one below (which invalidates on success).

const governanceFindingsKey = (projectId: string) => ["governance-findings", projectId] as const;

export function useGovernanceFindings(projectId: string, params: PageParams = {}) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...governanceFindingsKey(projectId), ...pageKeySuffix(params)],
    queryFn: () => client.listGovernanceFindings(projectId, params),
    enabled: Boolean(projectId),
    placeholderData: keepPreviousData,
  });
}

/** `governance_finding:resolve` is admin/operator only server-side (never the producer — ADR-0059
 * invariant 2). The UI gates the resolve button on the same, but the server enforces the real
 * decision (S-8). On success it invalidates the project's findings list so the resolved finding
 * leaves the open set. */
export function useResolveGovernanceFinding(projectId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<GovernanceFinding, ApiError, { id: string; note: string }>({
    mutationFn: ({ id, note }) => client.resolveGovernanceFinding(id, note),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: governanceFindingsKey(projectId) });
    },
  });
}

const governancePolicyKey = (projectId: string) => ["governance-policy", projectId] as const;

/**
 * A project's EFFECTIVE declared promotion policy (ADR-0058 introspect, #571) — the ONE source
 * of truth for whether governance findings are advisory or actually enforced for THIS project
 * (A-5). Backs the findings panel's "advisory vs enforced" banner: callers must render the
 * conservative advisory wording while `isPending`/`isError` (S-13-adjacent — never imply an
 * enforcement guarantee the fetch hasn't confirmed) and only claim ENFORCED once `data` names
 * the `promote.require.max_open_findings` predicate. No event stream: like the corpus/findings
 * reads, the policy only changes when the project's applied corpus snapshot advances (not from
 * this tab), so a plain fetch-on-mount is the right shape (P-1's "event-driven, not polled" is
 * about live server-pushed change, not about refusing every read that has no push source yet).
 */
export function useProjectGovernancePolicy(projectId: string) {
  const client = useApiClient();
  return useQuery<GovernancePolicy, ApiError>({
    queryKey: governancePolicyKey(projectId),
    queryFn: () => client.getGovernancePolicy(projectId),
    enabled: Boolean(projectId),
  });
}

export function useServices(projectId: string, params: PageParams = {}) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...queryKeys.services(projectId), ...pageKeySuffix(params)],
    queryFn: () => client.listServices({ projectId, ...params }),
    enabled: Boolean(projectId),
    // No event stream for services yet (P-1 seam, same gap as
    // containers) — poll faster while any row is mid-deploy so the list
    // catches the deploying -> running/error transition promptly.
    refetchInterval: (query) => {
      const items = query.state.data?.items;
      return items?.some((s) => s.status === "deploying") ? MID_DEPLOY_POLL_MS : 20_000;
    },
    placeholderData: keepPreviousData,
  });
}

export function useService(projectId: string, id: string | undefined) {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.service(projectId, id ?? ""),
    queryFn: () => client.getService(projectId, id as string),
    enabled: Boolean(projectId) && Boolean(id),
    refetchInterval: (query) => (query.state.data?.status === "deploying" ? MID_DEPLOY_POLL_MS : false),
  });
}

export function useCreateService(projectId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<Service, ApiError, CreateServiceInput>({
    mutationFn: (input) => client.createService(projectId, input),
    onSuccess: (service) => {
      queryClient.setQueryData(queryKeys.service(projectId, service.id), service);
      void queryClient.invalidateQueries({ queryKey: queryKeys.services(projectId) });
    },
  });
}

export function useUpdateService(projectId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<Service, ApiError, { id: string; input: UpdateServiceInput }>({
    mutationFn: ({ id, input }) => client.updateService(projectId, id, input),
    onSuccess: (service) => {
      queryClient.setQueryData(queryKeys.service(projectId, service.id), service);
      void queryClient.invalidateQueries({ queryKey: queryKeys.services(projectId) });
    },
  });
}

/**
 * Also the un-adopt mutation (ADR-0038 §3) — see `ApiClient.deleteService`'s doc
 * comment. Invalidates the containers list too: an un-adopted container's `managed`
 * flag doesn't actually change (adoption never relabels it — see `mockClient.ts`'s
 * adopt bookkeeping comment), but its container is once again eligible to be adopted
 * into a DIFFERENT service, so a stale "already adopted" 409 from a cached row would be
 * confusing without this.
 */
export function useDeleteService(projectId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, { id: string }>({
    mutationFn: ({ id }) => client.deleteService(projectId, id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.services(projectId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.containers() });
    },
  });
}

/**
 * Adopts an unmanaged container into a brand-new Service (ADR-0038 P3/P4b) — see
 * `ApiClient.adoptService`'s doc comment for the full request/response/error contract.
 * On success, seeds the new service's detail cache directly (the caller is about to
 * navigate straight to it — no need to wait on a refetch) and invalidates the
 * project's service list and the containers list (the adopted container, while its
 * `managed` flag is unchanged, is now bound and a second adopt attempt should surface
 * the server's real 409 rather than a stale cached row inviting a retry).
 */
export function useAdoptService(projectId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<Service, ApiError, AdoptServiceInput>({
    mutationFn: (input) => client.adoptService(projectId, input),
    onSuccess: (service) => {
      queryClient.setQueryData(queryKeys.service(projectId, service.id), service);
      void queryClient.invalidateQueries({ queryKey: queryKeys.services(projectId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.containers() });
    },
  });
}

/**
 * The READ-ONLY pre-persist adopt preview (issue #331) — see `ApiClient.adoptPreview`'s
 * doc comment. A mutation, not a query: it is triggered imperatively when the operator
 * advances to the review step (there is no cache to keep warm, and it must NOT run just by
 * mounting the modal), and it persists nothing, so there is nothing to invalidate.
 */
export function useAdoptPreview(projectId: string) {
  const client = useApiClient();
  return useMutation<AdoptPreview, ApiError, AdoptServiceInput>({
    mutationFn: (input) => client.adoptPreview(projectId, input),
  });
}

/**
 * `POST .../projects/{id}/compose/import` (ADR-0025 §2). On success,
 * invalidates both the project's services AND connections lists — a compose
 * import creates both in one call (see `ApiClient.importCompose`'s doc
 * comment), so both lists must catch up together. Refused 409 when the
 * project already has services, 400 with an itemized parse error otherwise —
 * both are rendered by the caller (`ImportComposeModal`), not retried here.
 */
export function useImportCompose(projectId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<ComposeImportResult, ApiError, string>({
    mutationFn: (composeText) => client.importCompose(projectId, composeText),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.services(projectId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.connections(projectId) });
    },
  });
}

/**
 * Triggers a real deploy (see `ApiClient.deployService`'s doc comment). The
 * mutation settles when the server ACCEPTS the attempt, not when the fleet is
 * done — the deployment comes back `status: "deploying"` and the queries below
 * poll it (and the service) until it reaches a terminal status.
 *
 * `onSettled` — not `onSuccess` — refreshes the service and its deploy history
 * on BOTH outcomes: a refused attempt still changes nothing server-side but a
 * failed one is recorded (a real row with a real `detail`), so the history and
 * the service's own status must catch up whether the call resolved or
 * rejected, never only on success.
 */
export function useDeployService(projectId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<Deployment, ApiError, { id: string }>({
    mutationFn: ({ id }) => client.deployService(projectId, id),
    onSettled: (_deployment, _error, { id }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.service(projectId, id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.services(projectId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.deployments(projectId, id) });
    },
  });
}

export function useDeployments(
  projectId: string,
  serviceId: string | undefined,
  params: PageParams = {},
) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...queryKeys.deployments(projectId, serviceId ?? ""), ...pageKeySuffix(params)],
    queryFn: () => client.listDeployments({ projectId, serviceId: serviceId as string, ...params }),
    enabled: Boolean(projectId) && Boolean(serviceId),
    refetchInterval: (query) => {
      const items = query.state.data?.items;
      return items?.some((d) => d.status === "pending" || d.status === "deploying") ? MID_DEPLOY_POLL_MS : false;
    },
    placeholderData: keepPreviousData,
  });
}

/**
 * One deployment's full detail — the timeline entry plus its per-placement
 * convergence breakdown (Host/Deploy UX Phase 2, #539). No event stream yet
 * (P-1 seam, same gap as every other list in this file) — polls while the
 * deployment hasn't reached a terminal status so the detail view shows LIVE
 * convergence for the active deploy, stopping the moment it settles.
 */
export function useDeployment(
  projectId: string,
  serviceId: string,
  deploymentId: string | undefined,
) {
  const client = useApiClient();
  return useQuery<DeploymentDetail>({
    queryKey: queryKeys.deployment(projectId, serviceId, deploymentId ?? ""),
    queryFn: () => client.getDeployment(projectId, serviceId, deploymentId as string),
    enabled: Boolean(projectId) && Boolean(serviceId) && Boolean(deploymentId),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === "pending" || status === "deploying" ? MID_DEPLOY_POLL_MS : false;
    },
  });
}

/** Re-applies a past deployment's exact artifact (Host/Deploy UX Phase 2,
 * #539) — see `ApiClient.redeployDeployment`'s doc comment for the
 * requires_promotion distinction from rollback. Invalidates the same
 * queries a fresh deploy does, since the accepted "deploying" handle
 * settles the same way. */
export function useRedeployDeployment(projectId: string, serviceId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<Deployment, ApiError, { deploymentId: string; options?: RedeployOptions }>({
    mutationFn: ({ deploymentId, options }) =>
      client.redeployDeployment(projectId, serviceId, deploymentId, options),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.service(projectId, serviceId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.services(projectId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.deployments(projectId, serviceId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.placements(projectId, serviceId) });
    },
  });
}

/** Rolls the service back to a past deployment's recorded target (ADR-0031
 * §2b) — see `ApiClient.rollbackDeployment`'s doc comment for why this is
 * NOT subject to requires_promotion, unlike redeploy. Invalidates the same
 * queries `useRedeployDeployment` does. */
export function useRollbackDeployment(projectId: string, serviceId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<Deployment, ApiError, { deploymentId: string; options?: RedeployOptions }>({
    mutationFn: ({ deploymentId, options }) =>
      client.rollbackDeployment(projectId, serviceId, deploymentId, options),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.service(projectId, serviceId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.services(projectId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.deployments(projectId, serviceId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.placements(projectId, serviceId) });
    },
  });
}

/**
 * The read-only deploy plan/volume-risk preview (ADR-0031 §2d) — `GET .../deploy/preview`.
 * `enabled` defaults to `false`: a caller opens this only when it's actually about to be
 * shown (the rollback dialog), never on every service-detail page load — it's cheap, but
 * there is nothing to preview until an operator is looking at a specific rollback.
 */
export function useDeployPreview(projectId: string, serviceId: string, enabled: boolean) {
  const client = useApiClient();
  return useQuery<DeployPreview, ApiError>({
    queryKey: queryKeys.deployPreview(projectId, serviceId),
    queryFn: () => client.getDeployPreview(projectId, serviceId),
    enabled: enabled && Boolean(projectId) && Boolean(serviceId),
  });
}

// ---- The Placement Graph (ADR-0025): placements ----
// No event stream yet (P-1 seam, same gap as every other list in this file
// today) — every mutation invalidates the relevant list(s) on success. A
// placement's own status is server-owned (S-15): the deploy path writes it as
// each node is worked on (ADR-0025 §4), which is why the list POLLS while any
// placement is mid-deploy, mirroring `useServices`/`useService`.

export function usePlacements(projectId: string, serviceId: string, params: PageParams = {}) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...queryKeys.placements(projectId, serviceId), ...pageKeySuffix(params)],
    queryFn: () => client.listPlacements({ projectId, serviceId, ...params }),
    enabled: Boolean(projectId) && Boolean(serviceId),
    // A deploy is accepted, not awaited (see `useDeployService`), so this list is
    // where an operator watches it happen node by node. Polling stops as soon as
    // no placement is mid-flight — a deployed fleet costs no extra requests.
    refetchInterval: (query) => {
      const items = query.state.data?.items;
      return items?.some((p) => p.status === "deploying" || p.status === "pending")
        ? MID_DEPLOY_POLL_MS
        : false;
    },
    placeholderData: keepPreviousData,
  });
}

/** The reverse lookup: every placement currently on one node, across every
 * service in the fleet (`GET /nodes/{nodeId}/placements`) — feeds a
 * node-centric view rather than a service-centric one. */
export function useNodePlacements(nodeId: string | undefined, params: PageParams = {}) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...queryKeys.nodePlacements(nodeId ?? ""), ...pageKeySuffix(params)],
    queryFn: () => client.listNodePlacements({ nodeId: nodeId as string, ...params }),
    enabled: Boolean(nodeId),
    placeholderData: keepPreviousData,
  });
}

export function useCreatePlacement(projectId: string, serviceId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<Placement, ApiError, CreatePlacementInput>({
    mutationFn: (input) => client.createPlacement(projectId, serviceId, input),
    onSuccess: (placement) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.placements(projectId, serviceId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.nodePlacements(placement.nodeId) });
    },
  });
}

export function useUpdatePlacement(projectId: string, serviceId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<Placement, ApiError, { placementId: string; input: UpdatePlacementInput }>({
    mutationFn: ({ placementId, input }) => client.updatePlacement(projectId, serviceId, placementId, input),
    onSuccess: (placement) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.placements(projectId, serviceId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.nodePlacements(placement.nodeId) });
    },
  });
}

export function useDeletePlacement(projectId: string, serviceId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  // The node a deleted placement lived on isn't known to the caller after
  // the fact, so its own nodePlacements cache can't be targeted precisely —
  // invalidating every nodePlacements entry (a broad prefix match, unlike
  // the narrower list above) is the honest fallback rather than leaving a
  // stale node-scoped view behind.
  return useMutation<void, ApiError, { placementId: string; nodeId: string }>({
    mutationFn: ({ placementId }) => client.deletePlacement(projectId, serviceId, placementId),
    onSuccess: (_void, { nodeId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.placements(projectId, serviceId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.nodePlacements(nodeId) });
    },
  });
}

// ---- The Placement Graph (ADR-0025): connections ----
// Immutable once created (no update hook, matching client.ts) — only
// list/create/delete.

export function useConnections(projectId: string, params: PageParams = {}) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...queryKeys.connections(projectId), ...pageKeySuffix(params)],
    queryFn: () => client.listConnections({ projectId, ...params }),
    enabled: Boolean(projectId),
    placeholderData: keepPreviousData,
  });
}

export function useCreateConnection(projectId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<Connection, ApiError, CreateConnectionInput>({
    mutationFn: (input) => client.createConnection(projectId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.connections(projectId) });
    },
  });
}

export function useDeleteConnection(projectId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, { connectionId: string }>({
    mutationFn: ({ connectionId }) => client.deleteConnection(projectId, connectionId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.connections(projectId) });
    },
  });
}

/**
 * Live reachability for EVERY connection in one project, in a single batch
 * call (ADR-0029 P3-5 — see `ConnectionStatus`'s doc comment in types.ts;
 * the real endpoint is `GET .../projects/{id}/connections/status`, project
 * scoped, not per-connection). `retry: false` plus a swallowed error are
 * deliberate — a failure (this endpoint racing P3-5's own merge, or any
 * other transport error) must degrade EVERY row to "unknown", never take
 * down the connections list it's attached to (mirrors `useRouteRows`'s
 * `placementsDegraded` posture for a secondary, non-blocking resolution).
 * Callers read `data`/`isError` as usual; `isError` here means "couldn't
 * get a live reading," which the caller should render as the neutral
 * unknown state for every connection, not an alarm.
 */
export function useConnectionStatuses(projectId: string) {
  const client = useApiClient();
  return useQuery<ConnectionStatus[], ApiError>({
    queryKey: queryKeys.connectionStatuses(projectId),
    queryFn: () => client.getConnectionStatuses({ projectId }),
    enabled: Boolean(projectId),
    retry: false,
    // Cheap enough (and stale enough to matter for an operator debugging
    // reachability right now) to check again on window refocus rather than
    // sit on a possibly-stale reading like the rest of this file's queries.
    refetchOnWindowFocus: true,
  });
}

// ---- Service domain bindings (ADR-0026 §3/§5) ----
// Immutable once created (no update hook, matching connections above) —
// rebinding a hostname is delete-then-create. Every mutation invalidates the
// owning SERVICE's domains list, scoped by serviceId — unlike connections
// (project-wide), a domain binding is only ever read per-service.

export function useDomains(projectId: string, serviceId: string, params: PageParams = {}) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...queryKeys.domains(projectId, serviceId), ...pageKeySuffix(params)],
    queryFn: () => client.listDomains(projectId, serviceId, params),
    enabled: Boolean(projectId) && Boolean(serviceId),
    placeholderData: keepPreviousData,
  });
}

export function useCreateDomain(projectId: string, serviceId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<Domain, ApiError, CreateDomainInput>({
    mutationFn: (input) => client.createDomain(projectId, serviceId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.domains(projectId, serviceId) });
    },
  });
}

export function useDeleteDomain(projectId: string, serviceId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, { domainId: string }>({
    mutationFn: ({ domainId }) => client.deleteDomain(projectId, serviceId, domainId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.domains(projectId, serviceId) });
    },
  });
}

// ---- Hero flow: provider credentials (write-only — S-13) ----

export function useProviderCredentials(params: PageParams = {}) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...queryKeys.providerCredentials(), ...pageKeySuffix(params)],
    queryFn: () => client.listProviderCredentials({ ...params }),
    enabled: true,
    placeholderData: keepPreviousData,
  });
}

export function useCreateProviderCredential() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<ProviderCredentialSummary, ApiError, CreateProviderCredentialInput>({
    mutationFn: (input) => client.createProviderCredential(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.providerCredentials() });
    },
  });
}

export function useRotateProviderCredential() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<ProviderCredentialSummary, ApiError, { id: string; input: RotateProviderCredentialInput }>({
    mutationFn: ({ id, input }) => client.rotateProviderCredential(id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.providerCredentials() });
    },
  });
}

export function useDeleteProviderCredential() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, { id: string }>({
    mutationFn: ({ id }) => client.deleteProviderCredential(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.providerCredentials() });
    },
  });
}

// ---- LLM provider credentials (write-only key material — S-13/S-14) ----

export function useLlmCredentials(params: PageParams = {}) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...queryKeys.llmCredentials(), ...pageKeySuffix(params)],
    queryFn: () => client.listLlmCredentials({ ...params }),
    enabled: true,
    placeholderData: keepPreviousData,
  });
}

export function useCreateLlmCredential() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<LlmCredentialSummary, ApiError, CreateLlmCredentialInput>({
    mutationFn: (input) => client.createLlmCredential(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.llmCredentials() });
    },
  });
}

export function useRotateLlmCredentialKey() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<LlmCredentialSummary, ApiError, { id: string; input: RotateLlmCredentialKeyInput }>({
    mutationFn: ({ id, input }) => client.rotateLlmCredentialKey(id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.llmCredentials() });
    },
  });
}

export function useUpdateLlmCredentialConfig() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<LlmCredentialSummary, ApiError, { id: string; input: UpdateLlmCredentialConfigInput }>({
    mutationFn: ({ id, input }) => client.updateLlmCredentialConfig(id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.llmCredentials() });
    },
  });
}

export function useDeleteLlmCredential() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, { id: string }>({
    mutationFn: ({ id }) => client.deleteLlmCredential(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.llmCredentials() });
    },
  });
}

// ---- Agentic workbench sessions ----

export function useAgentSessions(params: ListAgentSessionsParams = {}) {
  const client = useApiClient();
  return useQuery({
    // `local` gets its own key segment (like `unmanaged` on useContainers)
    // so the Bonnie dashboard's filtered view and the plain Workbench page's
    // unfiltered view cache separately.
    queryKey: [...queryKeys.agentSessions(), params.local ?? false, ...pageKeySuffix(params)],
    queryFn: () => client.listAgentSessions({ ...params }),
    enabled: true,
    placeholderData: keepPreviousData,
    refetchInterval: 3000,
  });
}

/** Fetches one session by id, polling like `useAgentSessions` does (there is
 * no event stream for a session's status/branch/dirty/changeSummary yet —
 * same P-1 gap noted throughout this file). Used by a single-session detail
 * view (the Develop conversational view) so it doesn't have to filter the
 * whole list to find one row. */
export function useAgentSession(id: string | undefined) {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.agentSession(id ?? ""),
    queryFn: () => client.getAgentSession(id as string),
    enabled: Boolean(id),
    refetchInterval: 3000,
  });
}

export function useCreateAgentSession() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<AgentSession, ApiError, CreateAgentSessionInput>({
    mutationFn: (input) => client.createAgentSession(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.agentSessions() });
    },
  });
}

export function useStopAgentSession() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<AgentSession, ApiError, { id: string }>({
    mutationFn: ({ id }) => client.stopAgentSession(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.agentSessions() });
    },
  });
}

export function useDeleteAgentSession() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, { id: string }>({
    mutationFn: ({ id }) => client.deleteAgentSession(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.agentSessions() });
    },
  });
}

/** Sends a task prompt to a running Bonnie session (ADR-0039 P6). No cache
 * write here beyond invalidation — the useful state this produces
 * (`dirty`/`changeSummary` moving) is server-derived and shows up through
 * the list's own poll (see useAgentSessions), never guessed client-side. */
export function useRunAgentSessionTask() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, { id: string; input: RunSessionTaskInput }>({
    mutationFn: ({ id, input }) => client.runAgentSessionTask(id, input),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.agentSessions() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.agentSession(id) });
    },
  });
}

/** Promotes a Bonnie session's worktree to a branch/PR (ADR-0039 P5/P6). */
export function usePromoteAgentSession() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<PromoteSessionResult, ApiError, { id: string; input: PromoteSessionInput }>({
    mutationFn: ({ id, input }) => client.promoteAgentSession(id, input),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.agentSessions() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.agentSession(id) });
    },
  });
}

/** The live server types/locations/prices a given credential can
 * provision — only fetched once a credential is actually selected (no
 * `credentialId` means nothing to ask the provider yet). */
export function useProviderCatalog(credentialId: string | undefined) {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.providerCatalog(credentialId ?? ""),
    queryFn: () => client.getProviderCatalog(credentialId as string),
    enabled: Boolean(credentialId),
  });
}

// ---- Hero flow: instances (Hetzner provisioning) ----

export function useInstances(projectId?: string, params: PageParams = {}) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...queryKeys.instances(projectId), ...pageKeySuffix(params)],
    queryFn: () => client.listInstances({ projectId, ...params }),
    enabled: true,
    // No event stream for instances yet (P-1 seam) — poll while any row is
    // still non-terminal, same shape as useServices above.
    refetchInterval: (query) => {
      const items = query.state.data?.items;
      return items?.some((i) => i.status === "requested" || i.status === "provisioning" || i.status === "deleting")
        ? MID_DEPLOY_POLL_MS
        : 20_000;
    },
    placeholderData: keepPreviousData,
  });
}

export function useInstance(id: string | undefined) {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.instance(id ?? ""),
    queryFn: () => client.getInstance(id as string),
    enabled: Boolean(id),
  });
}

export function useCreateInstance() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<Instance, ApiError, CreateInstanceInput>({
    mutationFn: (input) => client.createInstance(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.instances() });
    },
  });
}

export function useDeleteInstance() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, { id: string }>({
    mutationFn: ({ id }) => client.deleteInstance(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.instances() });
    },
  });
}

// ---- Org import: "bring your existing infra" (ADR-0048 §2, #472) ----
// Both are mutations, not queries: propose is a deliberate, on-demand read the
// operator triggers by picking a source (never auto-fetched/polled — its result
// feeds an editable selection, not cached list data), and apply is the confirm-and
// -commit step. Neither has a stable cache key worth keeping around.

export function useProposeImport() {
  const client = useApiClient();
  return useMutation<ImportProposal, ApiError, ProposeImportParams>({
    mutationFn: (params) => client.proposeImport(params),
  });
}

export function useApplyImport() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<ApplyImportResult, ApiError, ProposedImportItem[]>({
    mutationFn: (items) => client.applyImport(items),
    onSuccess: () => {
      // Apply creates projects (ADR-0048 §2) — invalidate the same list
      // useCreateProject does so the projects page reflects what import committed.
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects() });
    },
  });
}

// ---- Fleet health matrix (ADR-0030 P4-2, read-only) ----
// No mutations here by construction (see HealthCell's doc comment,
// types.ts) — DERIVED VIEW ONLY. No event stream yet either (P-1 seam, same
// gap as most of this file today); an operator refetches or reopens the
// page to see a fresher read.

export function useHealthMatrix(params: PageParams = {}) {
  const client = useApiClient();
  return useQuery<Page<HealthCell>, ApiError>({
    queryKey: [...queryKeys.health(), ...pageKeySuffix(params)],
    queryFn: () => client.listHealthMatrix({ ...params }),
    enabled: true,
    placeholderData: keepPreviousData,
  });
}

// ---- Fleet-health alerting (ADR-0030 P4-5) ----
// No event stream yet — every mutation invalidates the rules list on
// success, same "no event stream yet, invalidate and refetch" shape as
// secrets/provider-credentials above.

export function useAlertRules(params: PageParams = {}) {
  const client = useApiClient();
  return useQuery<Page<AlertRule>, ApiError>({
    queryKey: [...queryKeys.alertRules(), ...pageKeySuffix(params)],
    queryFn: () => client.listAlertRules({ ...params }),
    enabled: true,
    placeholderData: keepPreviousData,
  });
}

export function useCreateAlertRule() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<AlertRule, ApiError, CreateAlertRuleInput>({
    mutationFn: (input) => client.createAlertRule(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.alertRules() });
    },
  });
}

export function useUpdateAlertRule() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<AlertRule, ApiError, { id: string; input: UpdateAlertRuleInput }>({
    mutationFn: ({ id, input }) => client.updateAlertRule(id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.alertRules() });
    },
  });
}

export function useDeleteAlertRule() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, { id: string }>({
    mutationFn: ({ id }) => client.deleteAlertRule(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.alertRules() });
    },
  });
}

/** A rule's firing/resolved occurrences. `enabled` lets a caller only fetch
 * once a rule row is actually expanded, rather than fanning out one request
 * per rule on every list render. */
export function useAlertInstances(
  ruleId: string | undefined,
  params: PageParams = {},
  options?: { enabled?: boolean },
) {
  const client = useApiClient();
  return useQuery<Page<AlertInstance>, ApiError>({
    queryKey: [...queryKeys.alertInstances(ruleId ?? ""), ...pageKeySuffix(params)],
    queryFn: () => client.listAlertInstances({ ruleId: ruleId as string, ...params }),
    enabled: Boolean(ruleId) && (options?.enabled ?? true),
    placeholderData: keepPreviousData,
  });
}

// ---- First-class environments (ADR-0035 §1, M1 PR-E) ----
// Tenant/session-scoped (no tenant). No event stream yet (P-1 seam, same gap
// as every other list here) — every mutation invalidates the list on success.
// The server enforces authz on every call (create/update/delete admin-only,
// promote admin+operator); these hooks just surface whatever it answers.

export function useEnvironments(params: PageParams = {}, options?: { enabled?: boolean }) {
  const client = useApiClient();
  return useQuery<Page<Environment>>({
    queryKey: [...queryKeys.environments(), ...pageKeySuffix(params)],
    queryFn: () => client.listEnvironments(params),
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
  });
}

export function useEnvironment(id: string | undefined, options?: { enabled?: boolean }) {
  const client = useApiClient();
  return useQuery<Environment>({
    queryKey: queryKeys.environment(id ?? ""),
    queryFn: () => client.getEnvironment(id as string),
    enabled: Boolean(id) && (options?.enabled ?? true),
  });
}

export function useCreateEnvironment() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<Environment, ApiError, CreateEnvironmentInput>({
    mutationFn: (input) => client.createEnvironment(input),
    onSuccess: (env) => {
      queryClient.setQueryData(queryKeys.environment(env.id), env);
      void queryClient.invalidateQueries({ queryKey: queryKeys.environments() });
    },
  });
}

export function useUpdateEnvironment() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<Environment, ApiError, { id: string; patch: UpdateEnvironmentInput }>({
    mutationFn: ({ id, patch }) => client.updateEnvironment(id, patch),
    onSuccess: (env) => {
      queryClient.setQueryData(queryKeys.environment(env.id), env);
      void queryClient.invalidateQueries({ queryKey: queryKeys.environments() });
    },
  });
}

export function useDeleteEnvironment() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, { id: string }>({
    mutationFn: ({ id }) => client.deleteEnvironment(id),
    onSuccess: (_void, { id }) => {
      queryClient.removeQueries({ queryKey: queryKeys.environment(id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.environments() });
    },
  });
}

/**
 * Promotes a service into a TARGET environment (see `ApiClient.promoteService`).
 * ACCEPTED, not awaited — the deployment comes back "deploying". `onSettled`
 * refreshes the target service's deploy history and status on BOTH outcomes,
 * mirroring `useDeployService`: a refused promote changes nothing, but a
 * started one is a real deployment the history must catch up with. `tenant`
 * is threaded only to key the caches to invalidate — the promote call itself
 * is fleet-free.
 */
export function usePromoteService() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<Deployment, ApiError, { envId: string; body: PromoteServiceInput }>({
    mutationFn: ({ envId, body }) => client.promoteService(envId, body),
    onSettled: (_deployment, _error, { body }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.service(body.projectId, body.serviceId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.services(body.projectId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.deployments(body.projectId, body.serviceId) });
    },
  });
}

// ---- Billing (L-1) ----
// Reachable by admin and billing_admin; the server enforces that on every
// call, these hooks just surface whatever it answers (a 403 arrives as the
// same ApiError shape as anywhere else).

export function useSubscription(options?: { enabled?: boolean }) {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.subscription(),
    queryFn: () => client.getSubscription(),
    enabled: options?.enabled ?? true,
  });
}

/** Starts a checkout and resolves to the Stripe-hosted URL. The caller
 * navigates to it; it is a short-lived bearer capability, so it is never
 * stored, logged, or put in app state beyond the navigation itself (S-13). */
export function useStartCheckout() {
  const client = useApiClient();
  return useMutation<string, ApiError, { email?: string } | void>({
    mutationFn: (input) => client.startCheckout(input?.email),
  });
}

export function useOpenBillingPortal() {
  const client = useApiClient();
  return useMutation<string, ApiError, void>({
    mutationFn: () => client.openBillingPortal(),
  });
}

// ---- Licensing / entitlement (ADR-0017) ----
// `GET /api/entitlement` is `billing:view`, same reach as the subscription
// read above (admin + billing_admin) — callers MUST pass `enabled: false`
// for any other role (see EntitlementBanner/LicenseSection) so an
// operator/viewer never fires a request that can only answer 403.

export function useEntitlement(options?: { enabled?: boolean }) {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.entitlement(),
    queryFn: () => client.getEntitlement(),
    enabled: options?.enabled ?? true,
  });
}
